import { z } from "zod";

// TES-442 — LabPortalen (lab results) wire shapes shared by the API and admin.

// The remiss number the doctor may type for a LabOrder ("Remissnummer i
// Webdoc", optional). Loose on purpose: Ella cannot see Webdoc, and whether
// Webdoc's number equals LabPortalen's RequisitionID is open (O9). Trimmed;
// letters, digits and dashes; at most 64 characters.
export const REQUISITION_REF_MAX = 64;
export const requisitionRefSchema = z
  .string()
  .trim()
  .min(1)
  .max(REQUISITION_REF_MAX)
  .regex(/^[A-Za-z0-9-]+$/, { message: "requisition_ref_invalid" });

// Blank clears it: "" and null both mean "no remiss number".
export const optionalRequisitionRefSchema = z.union([
  z
    .string()
    .trim()
    .transform((s) => (s === "" ? null : s))
    .pipe(requisitionRefSchema.nullable()),
  z.null(),
]);

export const setLabOrderRequisitionRefBodySchema = z.object({
  requisitionRef: optionalRequisitionRefSchema,
});
export type SetLabOrderRequisitionRefBody = z.infer<
  typeof setLabOrderRequisitionRefBodySchema
>;

// Proposed staff copy, awaiting Emelie (TES-442). Swedish UI strings.
export const REQUISITION_REF_LABEL = "Remissnummer i Webdoc (valfritt)";
export const REQUISITION_REF_HELP =
  "Fyll i om du har det. Då kopplas provsvaret till rätt beställning direkt.";

export const labPortalenQuarantineReasonSchema = z.enum([
  "no_patient",
  "no_open_order",
  "ambiguous_multiple_orders",
  "patient_mismatch",
  "reservnummer_or_invalid_pnr",
  "unknown_requisition",
  "no_known_analytes",
  "order_feedback_sent",
  "unparseable",
  // TES-1341 — the message is not a lab report (Message@Type is not RPT).
  "not_a_report",
  // TES-1377 — the file held more than one Requisition or Reply; refused.
  "multiple_reports",
  // TES-1616 — attached, but these values have no panel analyte.
  "not_in_panel",
  // TES-1617 — attached, but these values have an unknown code, an
  // unexpected unit, or a text answer a factor cannot convert; or a mix
  // (values_not_stored). Each analysis says its own (unmappedReason).
  "unknown_code",
  "unit_mismatch",
  "unconvertible_text",
  "values_not_stored",
]);

// TES-1617 — why one value of a report was not stored on the order.
export const labPortalenUnmappedReasonSchema = z.enum([
  "unknown_code",
  "unit_mismatch",
  "not_in_panel",
  "unconvertible_text",
]);
export type LabPortalenUnmappedReason = z.infer<
  typeof labPortalenUnmappedReasonSchema
>;
export type LabPortalenQuarantineReason = z.infer<
  typeof labPortalenQuarantineReasonSchema
>;

export const labPortalenQuarantineStatusSchema = z.enum([
  "open",
  "attached",
  "dismissed",
]);

// One quarantined report as staff see it. The personnummer is masked to its
// birth date; `matchedPatientId` is the patient it resolves to today, if any,
// so staff can open her record without ever seeing the full number.
export const labPortalenQuarantineItemSchema = z.object({
  id: z.string(),
  interchangeRef: z.string(),
  reason: labPortalenQuarantineReasonSchema,
  status: labPortalenQuarantineStatusSchema,
  requisitionId: z.string().nullable(),
  externalRequisitionId: z.string().nullable(),
  personalNumberMasked: z.string().nullable(),
  matchedPatientId: z.string().nullable(),
  reportTime: z.string().nullable(),
  analyteCount: z.number().int(),
  analyses: z.array(
    z.object({
      testMethodCode: z.string().nullable(),
      anaName: z.string().nullable(),
      value: z.number().nullable(),
      valueText: z.string().nullable(),
      unit: z.string().nullable(),
      // Sample@LaboratoryID: the lab's own number for the sample (TES-1341:
      // NOT a code for the lab, as TES-1263 assumed). Optional so an older
      // API response still parses.
      laboratoryId: z.string().nullable().optional(),
      // TES-1341 — the lab that ran the analysis: Analysis>Lab@AddressCode,
      // else Requisition>SendUnit@AddressCode. A lab code, not personal
      // data. Optional so an older API response still parses.
      labCode: z.string().nullable().optional(),
      // TES-1617 — on a row holding the values an attached report could not
      // store: why this one was not. Null on a report quarantined whole.
      // Optional so an older API response still parses.
      unmappedReason: labPortalenUnmappedReasonSchema.nullable().optional(),
    }),
  ),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  // TES-1263 — how many of its analyses the current mappings read. Optional
  // so an older API response still parses.
  mappableAnalyteCount: z.number().int().optional(),
  // TES-1341 — the lab identified the patient only by a reservnummer
  // (Patient@ReservePatientID), which Ella never matches on; and the files
  // the lab attached (name and type only). Optional for older responses.
  hasReservePatientId: z.boolean().optional(),
  attachments: z
    .array(
      z.object({
        name: z.string().nullable(),
        type: z.string().nullable(),
        mimeType: z.string().nullable(),
      }),
    )
    .optional(),
});
export type LabPortalenQuarantineItem = z.infer<
  typeof labPortalenQuarantineItemSchema
>;

// TES-1179 — an OPEN quarantined report whose requisition id names a lab
// order (the same requisition rule as attribution rung 1). Shown on that
// order so the doctor sees that an answer arrived even though it could not be
// read in. Only what the order card needs: never the report's values, never
// the personnummer.
export const labOrderQuarantinedReportSchema = z.object({
  quarantineId: z.string(),
  reason: labPortalenQuarantineReasonSchema,
  // The lab's ReportTime, when the report carried one.
  reportTime: z.string().nullable(),
  // When the sweep first quarantined it.
  receivedAt: z.string(),
});
export type LabOrderQuarantinedReport = z.infer<
  typeof labOrderQuarantinedReportSchema
>;

// TES-1337 — the lab's own free-text comment on a whole order, from one
// LabPortalen delivery (interchange). An order split over several deliveries
// keeps one entry per delivery, oldest first. `level` is where it sat in the
// message (InfoSolutions 10.3: ReportHeader, ReqComment, InvestigationComment,
// SmpComment, ReportFooter; `reply` only on rows written before the spec was
// in hand), `type` the element's @Type. `text` is plain text: any HTML the lab
// sent has been reduced to text, and it is never rendered as HTML.
export const labOrderLabCommentSchema = z.object({
  interchangeRef: z.string(),
  reportTime: z.string().nullable(),
  level: z.enum([
    "header",
    "requisition",
    "investigation",
    "sample",
    "footer",
    "reply",
  ]),
  type: z.string().nullable().optional(),
  text: z.string(),
});
export type LabOrderLabComment = z.infer<typeof labOrderLabCommentSchema>;

// TES-1341 — a file the lab attached to a LabPortalen delivery
// (Reply>Attachment or Analysis>Attachment). Only that it exists, its name and
// type: the file is not stored in Ella, the doctor opens it in LabPortalen.
export const labOrderLabAttachmentSchema = z.object({
  interchangeRef: z.string(),
  reportTime: z.string().nullable(),
  name: z.string().nullable(),
  type: z.string().nullable(),
  mimeType: z.string().nullable(),
});
export type LabOrderLabAttachment = z.infer<typeof labOrderLabAttachmentSchema>;

// TES-1341 — the line admin shows where a lab attached a file. Proposed staff
// copy (the wording is from the ticket).
export const LAB_ATTACHMENT_IN_LABPORTALEN_LABEL = "Bilaga finns i LabPortalen";

// The line such an order shows instead of "Inväntar svar", on the Blodprov
// tab and as its work-queue title. Proposed staff copy (TES-1179).
export const UNREADABLE_LAB_REPORT_LABEL =
  "Svar mottaget – kunde inte läsas in";

export const labPortalenStatusSchema = z.object({
  mockMode: z.boolean(),
  syncEnabled: z.boolean(),
  cursor: z
    .object({
      highestId: z.number().int(),
      lastRunAt: z.string().nullable(),
      lastSuccessAt: z.string().nullable(),
      lastError: z.string().nullable(),
    })
    .nullable(),
  openQuarantine: z.number().int(),
  openQuarantineByReason: z.record(z.string(), z.number().int()),
  // "TestMethodCode|AnaName|Unit|reason" → count. Lab codes, never values.
  unknownAnalytes: z.record(z.string(), z.number().int()),
  // TES-1263 — the keys above that an active staff mapping now reads (the
  // counts are history; this says which are solved). Optional so an older
  // API response still parses.
  unknownAnalytesNowMapped: z.array(z.string()).optional(),
});
export type LabPortalenStatus = z.infer<typeof labPortalenStatusSchema>;

// TES-1265 — "Hämta nu": one sweep on demand, the same one the cron runs.
// Either it ran (with what it handled), or the reason it did not: sync is
// off, the integration has no tenant here, or a sweep is already running.
export const labPortalenSyncNowSkipReasonSchema = z.enum([
  "disabled",
  "not_configured",
  "already_running",
]);
export type LabPortalenSyncNowSkipReason = z.infer<
  typeof labPortalenSyncNowSkipReasonSchema
>;

export const labPortalenSyncNowResultSchema = z.discriminatedUnion("ran", [
  z.object({
    ran: z.literal(false),
    reason: labPortalenSyncNowSkipReasonSchema,
  }),
  z.object({
    ran: z.literal(true),
    // Reports handled this run: attached + quarantined.
    fetched: z.number().int(),
    attached: z.number().int(),
    quarantined: z.number().int(),
    unknownAnalytes: z.number().int(),
    // The sweep's sanitised error when it stopped early; what it handled
    // before that is kept (and counted above).
    error: z.string().nullable(),
  }),
]);
export type LabPortalenSyncNowResult = z.infer<
  typeof labPortalenSyncNowResultSchema
>;

// Attach or dismiss: a reason is required, and goes to the audit row
// (encrypted, never to metadata).
export const attachLabPortalenQuarantineBodySchema = z.object({
  labOrderId: z.string().uuid(),
  reason: z.string().trim().min(3).max(500),
});
export type AttachLabPortalenQuarantineBody = z.infer<
  typeof attachLabPortalenQuarantineBodySchema
>;

export const dismissLabPortalenQuarantineBodySchema = z.object({
  reason: z.string().trim().min(3).max(500),
});
export type DismissLabPortalenQuarantineBody = z.infer<
  typeof dismissLabPortalenQuarantineBodySchema
>;

// ---------------------------------------------------------------------------
// TES-1263 — staff-maintained analyte mappings: a lab's analysis code and/or
// name, in ONE reported unit, read as a panel analyte. Doctor and admin
// create and deactivate them; a coach never sees them.
// ---------------------------------------------------------------------------

// Blank and null both mean "not set".
const optionalText = (max: number) =>
  z
    .union([z.string(), z.null()])
    .optional()
    .transform((s) => {
      const t = typeof s === "string" ? s.trim() : "";
      return t === "" ? null : t;
    })
    .pipe(z.string().max(max).nullable());

export const LAB_ANALYTE_MAPPING_FACTOR_MAX = 1_000_000;

// A code or a name is enough to match, but one is required. No factor means
// the reported unit must BE the panel analyte's unit (the API checks it); a
// factor means value × factor is stored in the panel's unit.
export const createLabAnalyteMappingBodySchema = z
  .object({
    testMethodCode: optionalText(64),
    anaName: optionalText(200),
    // TES-1341 — the lab's code (Analysis>Lab@AddressCode / SendUnit@
    // AddressCode) to limit the mapping to one lab; null = any lab.
    labCode: optionalText(64),
    // LEGACY (TES-1263): Sample@LaboratoryID, a sample number, not a lab. No
    // longer accepted: a non-empty value is refused (laboratory_id_retired)
    // rather than silently dropped, which would widen the mapping to every
    // lab.
    laboratoryId: optionalText(64),
    reportedUnit: z.string().trim().min(1).max(32),
    analyteKey: z.string().trim().min(1).max(64),
    conversionFactor: z
      .number()
      .finite()
      .positive()
      .max(LAB_ANALYTE_MAPPING_FACTOR_MAX)
      .nullable()
      .optional()
      .transform((f) => f ?? null),
    reason: z.string().trim().min(3).max(500),
  })
  .refine((b) => b.testMethodCode !== null || b.anaName !== null, {
    message: "code_or_name_required",
    path: ["testMethodCode"],
  })
  .refine((b) => b.laboratoryId === null, {
    message: "laboratory_id_retired",
    path: ["laboratoryId"],
  });
export type CreateLabAnalyteMappingBody = z.input<
  typeof createLabAnalyteMappingBodySchema
>;

export const deactivateLabAnalyteMappingBodySchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

export const labAnalyteMappingSchema = z.object({
  id: z.string(),
  // TES-1341 — the lab's code the mapping is limited to. Optional so an
  // older API response still parses.
  labCode: z.string().nullable().optional(),
  // LEGACY sample-number scope of a mapping created before TES-1341 (see
  // createLabAnalyteMappingBodySchema). Null on every newer row.
  laboratoryId: z.string().nullable(),
  testMethodCode: z.string().nullable(),
  anaName: z.string().nullable(),
  reportedUnit: z.string(),
  analyteKey: z.string(),
  // The panel analyte's unit the value is stored in.
  targetUnit: z.string(),
  conversionFactor: z.number().nullable(),
  active: z.boolean(),
  reason: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  deactivatedAt: z.string().nullable(),
});
export type LabAnalyteMapping = z.infer<typeof labAnalyteMappingSchema>;

export const labAnalyteMappingListSchema = z.object({
  mappings: z.array(labAnalyteMappingSchema),
  // The analytes a mapping can point at, with the unit each is stored in.
  panel: z.array(z.object({ analyteKey: z.string(), unit: z.string() })),
});
export type LabAnalyteMappingList = z.infer<typeof labAnalyteMappingListSchema>;

// Re-run: attach every open no_known_analytes, no_open_order or no_patient
// report whose analyses the current mappings read, through the automatic
// attribution ladder (TES-1620: which now creates the order for a known
// patient with none).
export const rerunLabPortalenQuarantineBodySchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

export const rerunLabPortalenQuarantineResultSchema = z.object({
  attached: z.number().int(),
  // Reports whose analyses now map but whose attribution still fails; the
  // row now carries this reason, for a doctor to attach by hand.
  requarantined: z.array(
    z.object({ id: z.string(), reason: labPortalenQuarantineReasonSchema }),
  ),
  // Reports the current mappings still cannot read.
  unchanged: z.number().int(),
});
export type RerunLabPortalenQuarantineResult = z.infer<
  typeof rerunLabPortalenQuarantineResultSchema
>;
